"use client";

import { agentsFiltersAtom } from "@/app/(saas)/(app)/business-brokers/_components/filters/state";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { buildLocationUrlSegments } from "@/lib/locations";
import { getMapComponents, type SearchHookRetrieveResult } from "@repo/maps";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

export function AgentLocationSearchBox() {
  const t = useTranslations();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [filters] = useAtom(agentsFiltersAtom);
  const [searchValue, setSearchValue] = useState<string>("");

  const { SearchBox } = getMapComponents(
    process.env.NEXT_PUBLIC_MAPS_PROVIDER as any,
  );

  async function handleSearchResultRetrieved(result: SearchHookRetrieveResult) {
    if (result.locations.length === 0) {
      return;
    }

    if (result.locations.length > 0) {
      const firstLocation = result.locations[0];
      const countryCode = firstLocation.context.country?.code?.toLowerCase();
      if (!countryCode) {
        return;
      }

      const path = firstLocation.context.postcode
        ? `${countryCode}/${firstLocation.context.postcode.name}`
        : buildLocationUrlSegments(firstLocation);

      if (!path) {
        return;
      }

      const searchStr = searchParams.toString();
      const newPath = searchStr
        ? `/business-brokers/${path}?${searchStr}`
        : `/business-brokers/${path}`;

      router.push(newPath);
    }
  }

  useEffect(() => {
    if (!filters.location) {
      setSearchValue("");
    } else {
      setSearchValue(filters.location.name);
    }
  }, [filters.location]);

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-base">
        {t("saas.agentSearch.form.location.label")}
      </Label>
      <SearchBox
        token={process.env.NEXT_PUBLIC_MAP_TOKEN as string}
        types={[
          "region",
          "postcode",
          "district",
          "place",
          "locality",
          "neighborhood",
        ]}
        containerProps={{
          className: "flex-1 relative",
        }}
        resultsContainerProps={{
          className:
            "absolute top-12 md:top-16 left-0 w-full z-50 bg-card border rounded-md shadow-lg overflow-hidden",
        }}
        renderInput={({ updateSearchValue, displayResults, hideResults }) => (
          <div className="relative">
            <Icon
              icon="MagnifyingGlass"
              className="absolute top-1/2 left-4 -translate-y-1/2 opacity-50"
            />

            <Input
              value={searchValue}
              className="text-sm h-auto p-2 md:p-4 pl-12 md:pl-12"
              placeholder={t("saas.agentSearch.form.location.placeholder")}
              onChange={(e) => {
                setSearchValue(e.target.value);
                updateSearchValue(e.target.value);
              }}
              onFocus={displayResults}
              onBlur={hideResults}
            />
          </div>
        )}
        renderSuggestion={({ suggestion, onSelect }) => (
          <Button
            onClick={() => onSelect(suggestion)}
            variant="ghost"
            className="w-full h-auto flex-col items-start text-left rounded-none"
          >
            <strong>{suggestion.name}</strong>
            <div>{suggestion.formattedLocation}</div>
          </Button>
        )}
        renderEmpty={({ currentLocationButton }) => currentLocationButton}
        renderCurrentLocationButton={({ useCurrentLocation }) => (
          <Button
            onClick={(e) => {
              e.preventDefault();
              useCurrentLocation();
            }}
            variant="ghost"
            className="w-full h-auto justify-start rounded-none font-semibold py-4"
          >
            <Icon icon="Locate" />
            {t("saas.search.filters.fields.location.results.current")}
          </Button>
        )}
        onRetrieved={handleSearchResultRetrieved}
      />
    </div>
  );
}

AgentLocationSearchBox.displayName = "AgentLocationSearchBox";
